import { useEffect, useRef, useState } from "react";

import {
  downloadQuantstatHtml, downloadQuantstatHtmlFromCsv,
  getNavRange, listBenchmarks, listProducts,
} from "@/lib/api";
import type { MpInfo, NavRange } from "@/types/api";

// Sentinel value used by the benchmark <select>. Empty string is what
// the backend takes (== "no benchmark"); we keep it as a constant so
// the markup reads better.
const NO_BENCHMARK = "";

export function StatsPage() {
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-ink-mute">Stats</h1>
      <MpCodeQuantstatPanel />
      <CsvQuantstatPanel />
    </div>
  );
}

// ─────────────────────────────────────────────────── mp_code → quantstat
function MpCodeQuantstatPanel() {
  const [products, setProducts] = useState<MpInfo[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>("");
  const [benchmarks, setBenchmarks] = useState<string[]>([]);
  const [bm, setBm] = useState<string>(NO_BENCHMARK);
  const [bmLoading, setBmLoading] = useState(false);
  const [navRange, setNavRange] = useState<NavRange | null>(null);
  const [start, setStart] = useState<string>("");
  const [end, setEnd] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listProducts()
      .then((d) => { if (!cancelled) { setProducts(d); setSelected(d[0]?.mp_code ?? ""); } })
      .catch((e) => {
        if (cancelled) return;
        setListError(extractDetail(e, "Failed to load mp_codes."));
      });
    return () => { cancelled = true; };
  }, []);

  // When the picked mp_code changes, refresh its benchmark options and
  // default to the first one. Falls back to "no benchmark" when the
  // mp_code has none on file.
  useEffect(() => {
    if (!selected) { setBenchmarks([]); setBm(NO_BENCHMARK); return; }
    let cancelled = false;
    setBmLoading(true);
    listBenchmarks(selected)
      .then((bms) => {
        if (cancelled) return;
        setBenchmarks(bms);
        setBm(bms[0] ?? NO_BENCHMARK);
      })
      .catch(() => {
        if (cancelled) return;
        setBenchmarks([]);
        setBm(NO_BENCHMARK);
      })
      .finally(() => { if (!cancelled) setBmLoading(false); });
    return () => { cancelled = true; };
  }, [selected]);

  // NAV date bounds for the picked mp_code — defaults the pickers to the
  // full range and clamps them (min/max) to dates that actually have data.
  useEffect(() => {
    if (!selected) { setNavRange(null); setStart(""); setEnd(""); return; }
    let cancelled = false;
    getNavRange(selected)
      .then((rng) => {
        if (cancelled) return;
        setNavRange(rng);
        setStart(rng.first ?? "");
        setEnd(rng.last ?? "");
      })
      .catch(() => {
        if (cancelled) return;
        setNavRange(null);
        setStart("");
        setEnd("");
      });
    return () => { cancelled = true; };
  }, [selected]);

  const rangeInvalid = Boolean(start && end && start > end);

  const onDownload = async () => {
    if (!selected || rangeInvalid) return;
    setBusy(true); setError(null);
    try {
      // Empty start/end → backend defaults (inception / latest).
      // ``bm === NO_BENCHMARK`` → strategy-only report.
      await downloadQuantstatHtml(selected, bm || null, start || undefined, end || undefined);
    } catch (e: unknown) {
      setError(await extractBlobDetail(e, "Download failed."));
    } finally { setBusy(false); }
  };

  return (
    <Panel title="By mp_code">
      {listError && <ErrBox msg={listError} />}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col min-w-[220px]">
          <label className="text-xs uppercase tracking-wider text-ink-dim mb-1">
            mp_code
          </label>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={!products || products.length === 0}
            className="rounded-md bg-panel-2 border border-line px-3 py-2 text-ink font-mono focus:border-accent focus:ring-2 focus:ring-accent/30 outline-none disabled:opacity-50"
          >
            {(products ?? []).map((p) => (
              <option key={p.mp_code} value={p.mp_code}>{p.mp_code}</option>
            ))}
            {products && products.length === 0 && (
              <option value="">— no mp_codes —</option>
            )}
          </select>
        </div>
        <div className="flex flex-col min-w-[200px]">
          <label className="text-xs uppercase tracking-wider text-ink-dim mb-1">
            Benchmark
          </label>
          <select
            value={bm}
            onChange={(e) => setBm(e.target.value)}
            disabled={bmLoading || !selected}
            className="rounded-md bg-panel-2 border border-line px-3 py-2 text-ink font-mono focus:border-accent focus:ring-2 focus:ring-accent/30 outline-none disabled:opacity-50"
          >
            {benchmarks.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
            <option value={NO_BENCHMARK}>no benchmark</option>
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-xs uppercase tracking-wider text-ink-dim mb-1">
            Start
          </label>
          <input
            type="date"
            value={start}
            min={navRange?.first ?? undefined}
            max={end || (navRange?.last ?? undefined)}
            onChange={(e) => setStart(e.target.value)}
            disabled={!selected || !navRange?.first}
            className="rounded-md bg-panel-2 border border-line px-3 py-2 text-ink font-mono focus:border-accent focus:ring-2 focus:ring-accent/30 outline-none disabled:opacity-50"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs uppercase tracking-wider text-ink-dim mb-1">
            End
          </label>
          <input
            type="date"
            value={end}
            min={start || (navRange?.first ?? undefined)}
            max={navRange?.last ?? undefined}
            onChange={(e) => setEnd(e.target.value)}
            disabled={!selected || !navRange?.last}
            className="rounded-md bg-panel-2 border border-line px-3 py-2 text-ink font-mono focus:border-accent focus:ring-2 focus:ring-accent/30 outline-none disabled:opacity-50"
          />
        </div>
        <button
          type="button"
          onClick={onDownload}
          disabled={busy || !selected || rangeInvalid}
          className="px-4 py-2 rounded-md bg-accent text-white font-semibold hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? "Generating…" : "↓ Quantstat"}
        </button>
      </div>
      {rangeInvalid && <ErrBox msg="Start date must be on or before the end date." />}
      {error && <ErrBox msg={error} />}
      <p className="text-xs text-ink-dim mt-3">
        Builds the quantstats HTML from
        <code className="mx-1 font-mono">tr_index_inception</code>
        for the selected mp_code and period — dates are clamped to where
        NAV data exists. Pick a benchmark to overlay it; pick
        "no benchmark" to render the strategy on its own.
      </p>
    </Panel>
  );
}

// ─────────────────────────────────────────────────── CSV upload → quantstat
function CsvQuantstatPanel() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const f = e.target.files?.[0] ?? null;
    setFile(f);
  };

  const onGenerate = async () => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      await downloadQuantstatHtmlFromCsv(file, title || undefined);
    } catch (e: unknown) {
      setError(await extractBlobDetail(e, "Upload failed."));
    } finally { setBusy(false); }
  };

  return (
    <Panel title="From a CSV upload">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col min-w-[240px]">
          <label className="text-xs uppercase tracking-wider text-ink-dim mb-1">
            CSV file
          </label>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={onPickFile}
            className="text-sm text-ink file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-accent file:text-white file:font-medium file:cursor-pointer hover:file:bg-accent/90"
          />
        </div>
        <div className="flex flex-col min-w-[200px]">
          <label className="text-xs uppercase tracking-wider text-ink-dim mb-1">
            Title (optional)
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="defaults to filename"
            className="rounded-md bg-panel-2 border border-line px-3 py-2 text-ink placeholder:text-ink-dim focus:border-accent focus:ring-2 focus:ring-accent/30 outline-none"
          />
        </div>
        <button
          type="button"
          onClick={onGenerate}
          disabled={busy || !file}
          className="px-4 py-2 rounded-md bg-accent text-white font-semibold hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? "Generating…" : "↓ Quantstat"}
        </button>
      </div>
      {error && <ErrBox msg={error} />}
      <div className="text-xs text-ink-dim mt-3 space-y-1">
        <p>Expected CSV shape (first row is the header and is skipped):</p>
        <ul className="list-disc pl-5">
          <li>column 0 — date</li>
          <li>column 1 — strategy NAV / index level</li>
          <li>column 2 (optional) — benchmark NAV in the same units</li>
        </ul>
        <p>
          quantstats converts NAV → returns internally, so the absolute scale
          doesn't matter — the cumulative-return CSV exported from a product's
          chart works as-is.
        </p>
      </div>
    </Panel>
  );
}

// ──────────────────────────────────────────────────────────── atoms
function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-md bg-panel border border-line/60 px-6 py-5">
      <h3 className="text-sm uppercase tracking-wider text-ink-mute mb-4">{title}</h3>
      {children}
    </section>
  );
}

function ErrBox({ msg }: { msg: string }) {
  return (
    <div className="mt-3 rounded-md bg-down/10 border border-down/30 px-3 py-2 text-sm text-down">
      {msg}
    </div>
  );
}

function extractDetail(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err?.response?.data?.detail ?? err?.message ?? fallback;
}

// Axios with responseType=blob serves error bodies as Blob too, so we
// have to read it back as JSON to recover the FastAPI detail.
async function extractBlobDetail(e: unknown, fallback: string): Promise<string> {
  const err = e as { response?: { data?: Blob | { detail?: string } }; message?: string };
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const txt = await data.text();
      const j = JSON.parse(txt) as { detail?: string };
      if (j.detail) return j.detail;
    } catch { /* fall through */ }
  } else if (data && typeof data === "object" && "detail" in data) {
    return (data as { detail?: string }).detail ?? fallback;
  }
  return err?.message ?? fallback;
}
